import * as React from 'react';
import cx from 'classnames';
import wait from 'utils/wait';
import Text from 'components/Text';
import openPopup from 'utils/openPopup';
import Image from 'components/Image';
import Glyph from 'components/Glyph';
import Button from 'components/ButtonDeprecated';
import type { ButtonColorUnion } from 'components/ButtonDeprecated';
import EventTracking from 'lib/EventTracking';
import type { GlyphName } from '@remind/r101-glyphs';
import LoadingIndicator from 'components/LoadingIndicator';
import { settingsForProvider } from '../../components/lib/AccountLinkingWindowSettings';
import { diffSourcesByLinked, showAccountLinkingToastsForDiff } from 'utils/accountLinkingToast';
import UploadButton from 'components/UploadButton';
import type { UploadRequestHandler, ThumbnailHandler } from 'lib/fileUploadHelper';
import CurrentUser from 'auth/CurrentUser';

import './styles/AccountLinkingComposerSidebarServices.scss';

type ContentSourceLinkedAccount = {
  authUrl: string;
  providerKey: string;
  isLinked: boolean;
};

export type ContentSource = {
  title: string;
  providerKey: string;
  iconURL: string;
  linkedAccountInfo?: ContentSourceLinkedAccount;
};

type Props = {
  error?: Error;
  loading: boolean;
  sources?: Array<ContentSource> | null;
  onViewContent: (contentSource: any) => void;
  reloadContentSources: (a: void) => void;
  uploadRequest: UploadRequestHandler;
  loadThumbnail: ThumbnailHandler;
  disabledUploads: boolean;
};

type State = {
  linkingSource: any;
};

class AccountLinkingComposerSidebarServices extends React.Component<Props, State> {
  state = {
    linkingSource: null,
  };

  componentWillUpdate(nextProps: Props) {
    ((oldSources, newSources) => {
      if (!oldSources || !newSources) {
        return;
      }

      const diff = diffSourcesByLinked(oldSources, newSources);
      showAccountLinkingToastsForDiff(diff);

      // @ts-expect-error ts-migrate(2339) FIXME: Property 'providerKey' does not exist on type 'nev... Remove this comment to see the full error message
      if (this.state.linkingSource && diff.newlyLinked[this.state.linkingSource.providerKey]) {
        this.props.onViewContent(this.state.linkingSource);
      }
    })(this.props.sources, nextProps.sources);
  }

  linkAccount(service: any) {
    // if not linkable account or already linked, show content
    if (!service.linkedAccountInfo || service.linkedAccountInfo.isLinked) {
      this.props.onViewContent(service);
      EventTracking.trackKey('account_linking.composer.content_provider.click', {
        provider: service.providerKey,
        composer_version: 'web_modal',
      });
    } else {
      // otherwise link the account
      const popup = openPopup(
        `${service.linkedAccountInfo.authUrl}`,
        `Link with ${service.title}`,
        settingsForProvider(service.linkedAccountInfo.providerKey)
      );

      // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
      wait(() => popup.closed).then(this.props.reloadContentSources, (e) => {
        console.debug(e, e && e.stack);
        console.error('Error while waiting for account linking popup to close');
        // Reload content sources anyway - hope for the best
        this.props.reloadContentSources();
      });

      EventTracking.trackKey('account_linking.composer.connect.click', {
        provider: service.providerKey,
        composer_version: 'web_modal',
      });

      this.setState({
        linkingSource: service,
      });
    }
  }

  renderUploadButton(
    text: string,
    color: ButtonColorUnion,
    glyph: GlyphName,
    eventKey: string,
    disabled: boolean
  ) {
    const { uploadRequest, loadThumbnail, disabledUploads } = this.props;
    let multiAttachmentLimit = 1;
    if (CurrentUser.isSignedIn()) {
      const currentUser = CurrentUser.getUser();
      multiAttachmentLimit =
        currentUser.attributes?.settings?.messages?.files?.multi_attachment_limit ?? 1;
    }
    return (
      <UploadButton
        fileContextKey=""
        className="AccountLinkingComposerSidebarServices-button"
        disabled={disabledUploads}
        // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
        eventKey={eventKey}
        eventProperties={{
          composer_version: 'web_modal',
        }}
        level="primary"
        weight="normal"
        color={color}
        uploadRequest={uploadRequest}
        loadThumbnail={loadThumbnail}
        multiple={multiAttachmentLimit > 1}
      >
        <div
          className={cx(
            'AccountLinkingComposerSidebarServices-button-icon',
            'AccountLinkingComposerSidebarServices-button-icon--rounded',
            `AccountLinkingComposerSidebarServices-button-icon--${color}`
          )}
        >
          <Glyph
            className="AccountLinkingComposerSidebarServices-button-icon-glyph"
            name={glyph}
            size="large"
            solo
          />
        </div>
        <span
          className={cx(
            { disabled: disabledUploads },
            'AccountLinkingComposerSidebarServices-button-text'
          )}
        >
          {text}
        </span>
      </UploadButton>
    );
  }

  renderServiceButton = (service: any) => {
    return (
      <Button
        key={service.providerKey}
        className="AccountLinkingComposerSidebarServices-button"
        level="primary"
        weight="normal"
        data-test="service-button"
        onClick={() => this.linkAccount(service)}
        eventElementName={`AccountLinkingComposerSidebarServices-button-${service.providerKey}`}
      >
        <div className="AccountLinkingComposerSidebarServices-button-icon">
          {service.linkedAccountInfo && !service.linkedAccountInfo.isLinked && (
            <div className="AccountLinkingComposerSidebarServices-button-icon-plus">
              <Glyph
                className="AccountLinkingComposerSidebarServices-button-icon-plus-glyph"
                size="xxxsmall"
                name="plus"
                color="mascot"
              />
            </div>
          )}
          <Image
            src={service.iconURL}
            className="AccountLinkingComposerSidebarServices-button-icon-image"
          />
        </div>
        <span className="AccountLinkingComposerSidebarServices-button-text">{service.title}</span>
      </Button>
    );
  };

  renderServices(): React.ReactElement | Array<React.ReactElement> | null {
    const { sources, loading } = this.props;

    if (loading) {
      return (
        <LoadingIndicator
          className="AccountLinkingComposerSidebarServices-loading"
          align="center"
        />
      );
    }

    if (sources) {
      return sources.map(this.renderServiceButton);
    } else {
      return null;
    }
  }

  render() {
    return (
      <div className="AccountLinkingComposerSidebarServices">
        <Text align="left" size="medium" color="chalkboard" weight="semibold">
          Add to your message
        </Text>
        <div className="AccountLinkingComposerSidebarServices-content">
          {this.renderUploadButton(
            'Upload photo or video',
            'leaf',
            'image',
            'announcements.announcement_modal.universal_composer.upload_photo.click',
            false
          )}
          {this.renderUploadButton(
            'Upload a file',
            'pool',
            'paperclip',
            'announcements.announcement_modal.universal_composer.upload_file.click',
            false
          )}
          {this.renderServices()}
        </div>
      </div>
    );
  }
}

export { AccountLinkingComposerSidebarServices };
export default AccountLinkingComposerSidebarServices;
